import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiHome, FiChevronRight, FiMail, FiPhone, FiMapPin, FiClock } from 'react-icons/fi';
import axios from 'axios';

// Hero Section
const HeroSection = styled.section`
  background-image: linear-gradient(rgba(1, 103, 95, 0.85), rgba(1, 103, 95, 0.85)), 
                    url('/Background Image 1.webp');
  background-size: cover;
  background-position: center;
  color: white;
  padding: 6rem 1rem;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  /* iPad Portrait */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    padding: 5rem 1rem;
    background-position: center 30%;
  }

  /* iPad Landscape */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
    padding: 4.5rem 1rem;
    background-position: center 25%;
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;
    background-position: center 20%;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  position: relative;
  display: inline-block;
  color: white;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: white;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const BreadcrumbLink = styled(Link)`
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled(FiChevronRight)`
  font-size: 1rem;
`;

const BreadcrumbCurrent = styled.span`
  font-weight: 700;
`;

// Main Content Section
const ContactContainer = styled.section`
  padding: 6rem 1rem;
  background: #f9f9f9;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  }
`;

const ContactContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  padding: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    padding: 2rem;
  }
`;

const ContactImageWrapper = styled.div`
  @media (max-width: 992px) {
    order: 2;
  }
`;

const ContactImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const ContactFormWrapper = styled.div`
  @media (max-width: 992px) {
    order: 1;
  }
`;

const ContactLabel = styled.h3`
  color: ${props => props.theme.colors.secondary};
  font-size: 1.1rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: inline-block;
  padding: 0.25rem 1rem;
  background-color: rgba(143, 226, 0, 0.1);
  border-radius: 20px;
`;

const ContactHeading = styled.h2`
  color: ${props => props.theme.colors.heading};
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 60px;
    height: 4px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ContactDescription = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const ContactForm = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  grid-column: ${props => props.fullWidth ? '1 / -1' : 'auto'};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  background-color: #f9f9f9;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
    outline: none;
    background-color: white;
  }
  
  &::placeholder {
    color: #aaa;
    font-weight: 400;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  min-height: 180px;
  resize: vertical;
  transition: all 0.3s ease;
  background-color: #f9f9f9;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(1, 103, 95, 0.1);
    outline: none;
    background-color: white;
  }
  
  &::placeholder {
    color: #aaa;
    font-weight: 400;
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.primary};
  font-weight: 800;
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  grid-column: 1 / -1;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 10px rgba(143, 226, 0, 0.3);
  
  &:hover {
    background-color: ${props => props.theme.colors.secondaryDark};
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(143, 226, 0, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

// Map Section
// Contact Info Section
const ContactInfoSection = styled.section`
  padding: 5rem 1rem;
  background: white;
`;

const ContactInfoContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ContactInfoTitle = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
  }
`;

const ContactInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const ContactInfoCard = styled.div`
  background: #f9f9f9;
  border-radius: 10px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ContactInfoIcon = styled.div`
  width: 70px;
  height: 70px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 1.75rem;
`;

const ContactInfoCardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.heading};
`;

const ContactInfoText = styled.p`
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
`;

const ContactLink = styled.a`
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.colors.primary};
    text-decoration: underline;
  }
`;

const MapSection = styled.section`
  padding: 0;
  background: #f5f5f5;
  position: relative;
  margin-top: 0;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 550px;
  border: none;
  position: relative;
  overflow: hidden;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    message: ''
  });
  
  const [siteSettings, setSiteSettings] = useState({
    contactInfo: {
      email: 'admin@rplsupport.com.au',
      phone: '+61 433 098 399',
      phoneSecondary: '+61 415 820 320',
      businessHours: 'Mon - Sat: 8:00 am - 7:00 pm',
      address: '200 Gilchrist Dr, Campbelltown NSW 2560'
    },
    socialMedia: {
      facebook: '#',
      instagram: '#',
      youtube: '#',
      linkedin: '#'
    }
  });
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get('/api/public/site-settings');
        if (response.data) {
          setSiteSettings(response.data);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };
    
    fetchSettings();
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post('/api/email/contact', formData);
      
      if (response.data.success) {
        alert('Thank you for your message. We will get back to you soon!');
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          message: ''
        });
      } else {
        alert('There was an error sending your message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('There was an error sending your message. Please try again.');
    }
  };

  return (
    <>
      <HeroSection>
        <HeroContent>
          <HeroTitle>Contact Us</HeroTitle>
          <Breadcrumbs>
            <BreadcrumbLink to="/">
              <FiHome style={{ marginRight: '5px' }} />
              Home
            </BreadcrumbLink>
            <BreadcrumbSeparator />
            <BreadcrumbCurrent>Contact Us</BreadcrumbCurrent>
          </Breadcrumbs>
        </HeroContent>
      </HeroSection>
      
      <ContactContainer>
        <ContactContent>
          <ContactImageWrapper>
            <ContactImage 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80" 
              alt="Student preparing for qualification" 
            />
          </ContactImageWrapper>
          
          <ContactFormWrapper>
            <ContactLabel>Contact Us</ContactLabel>
            <ContactHeading>Book Your Free Consultation Today!</ContactHeading>
            <ContactDescription>
              Discover how your experience can turn into formal qualifications — and take your career to the next level.
            </ContactDescription>
            
            <ContactForm onSubmit={handleSubmit}>
              <FormGroup>
                <FormInput 
                  type="text" 
                  name="firstName" 
                  placeholder="First Name" 
                  value={formData.firstName}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormInput 
                  type="text" 
                  name="lastName" 
                  placeholder="Last Name" 
                  value={formData.lastName}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormInput 
                  type="tel" 
                  name="phone" 
                  placeholder="Phone Number" 
                  value={formData.phone}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormInput 
                  type="email" 
                  name="email" 
                  placeholder="Email Address" 
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup fullWidth>
                <FormTextarea 
                  name="message" 
                  placeholder="Type Your Message" 
                  value={formData.message}
                  onChange={handleChange}
                  required 
                />
              </FormGroup>
              
              <SubmitButton type="submit">Send Message</SubmitButton>
            </ContactForm>
          </ContactFormWrapper>
        </ContactContent>
      </ContactContainer>
      
      <ContactInfoSection>
        <ContactInfoContainer>
          <ContactInfoTitle>Get In Touch</ContactInfoTitle>
          <ContactInfoGrid>
            <ContactInfoCard>
              <ContactInfoIcon>
                <FiMail />
              </ContactInfoIcon>
              <ContactInfoCardTitle>Email Us</ContactInfoCardTitle>
              <ContactInfoText>
                <ContactLink href={`mailto:${siteSettings.contactInfo.email}`}>
                  {siteSettings.contactInfo.email}
                </ContactLink>
              </ContactInfoText>
            </ContactInfoCard>
            
            <ContactInfoCard>
              <ContactInfoIcon>
                <FiPhone />
              </ContactInfoIcon>
              <ContactInfoCardTitle>Call Us</ContactInfoCardTitle>
              <ContactInfoText>
                <ContactLink href={`tel:${siteSettings.contactInfo.phone}`}>
                  {siteSettings.contactInfo.phone}
                </ContactLink><br />
                <ContactLink href={`tel:${siteSettings.contactInfo.phoneSecondary}`}>
                  {siteSettings.contactInfo.phoneSecondary}
                </ContactLink>
              </ContactInfoText>
            </ContactInfoCard>
            
            <ContactInfoCard>
              <ContactInfoIcon>
                <FiMapPin />
              </ContactInfoIcon>
              <ContactInfoCardTitle>Visit Us</ContactInfoCardTitle>
              <ContactInfoText>
                {siteSettings.contactInfo.address || '200 Gilchrist Dr, Campbelltown NSW 2560'}
              </ContactInfoText>
            </ContactInfoCard>
            
            <ContactInfoCard>
              <ContactInfoIcon>
                <FiClock />
              </ContactInfoIcon>
              <ContactInfoCardTitle>Business Hours</ContactInfoCardTitle>
              <ContactInfoText>{siteSettings.contactInfo.businessHours}</ContactInfoText>
            </ContactInfoCard>
          </ContactInfoGrid>
        </ContactInfoContainer>
      </ContactInfoSection>
      
      <MapSection>
        <MapContainer>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3312.1234567890123!2d150.81234567890123!3d-34.06543210987654!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b1d4c1234567890%3A0x1234567890abcdef!2s200%20Gilchrist%20Dr%2C%20Campbelltown%20NSW%202560%2C%20Australia!5e0!3m2!1sen!2sau!4v1234567890123"
            width="100%"
            height="400"
            style={{ border: 0, borderRadius: '12px' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="RPL Support Location"
          ></iframe>
        </MapContainer>
      </MapSection>
    </>
  );
};

export default ContactPage; 